import type { LeaguePublic } from "@softmax/observatory-client/types";
import { z } from "zod";
import { leagueRoute, slugifyLeagueName } from "@softmax/web-features/leagues";
import {
  curatedCoworldName,
  curatedLeagueName,
} from "@softmax/web-features/coworlds";
import { coverArtOf } from "./cover-art";
import type { PlayLeague } from "./types";
function cadenceMinutesOf(league: {
  settings?: Record<string, unknown> | null;
}): number | null {
  const settings = league.settings ?? null;
  const direct = settings?.round_interval_minutes;

  if (typeof direct === "number") return direct;

  const commissionerConfig = settings?.commissioner_config;
  const fallback =
    commissionerConfig &&
    typeof commissionerConfig === "object" &&
    "schedule_interval_minutes" in commissionerConfig
      ? commissionerConfig.schedule_interval_minutes
      : undefined;
  return typeof fallback === "number" ? fallback : null;
}
export function playLeagueOf({
  league,
  divisionId,
  divisionName,
}: {
  readonly league: LeaguePublic;
  readonly divisionId: string | null;
  readonly divisionName: string;
}): PlayLeague {
  const route = leagueRoute(league);
  const corpus = slugifyLeagueName(
    league.game.coworld_name ?? league.game.name,
  );
  return {
    route,
    id: league.id,
    name: curatedLeagueName(league),
    slug: [corpus, route.league].filter(Boolean).join("/"),
    gameName:
      curatedCoworldName(league.game.coworld_name ?? league.game.name) ??
      league.game.name,
    coworldName: league.game.coworld_name ?? null,
    coverArt: coverArtOf(corpus),
    season:
      typeof league.settings?.season === "string"
        ? league.settings.season
        : null,
    endsAt:
      typeof league.settings?.ends_at === "string"
        ? league.settings.ends_at
        : null,
    divisionId: divisionId ?? "",
    divisionName,
    cadenceMinutes: cadenceMinutesOf(league),
    createdAt: league.created_at ?? null,
    scoreLabel: "Score",
    scoringLabel: scoringLabelOf(league.settings ?? null),
    scoringRule: null,
  };
}
const rankingSchema = z.discriminatedUnion("algorithm", [
  z.object({ algorithm: z.literal("elo") }),
  z.object({ algorithm: z.literal("openskill") }),
  z.object({
    algorithm: z.literal("score"),
    window_rounds: z.number().nullish(),
    standing_aggregation: z.enum([
      "ewma",
      "mean",
      "max",
      "min",
      "rated",
      "latest",
    ]),
  }),
]);
const ladderSchema = z
  .object({
    enabled: z.boolean(),
    ranking: rankingSchema.nullish(),
  })
  .nullish();

function scoringLabelOf(
  settings: Readonly<Record<string, unknown>> | null,
): string {
  const ladder = ladderSchema.parse(settings?.ladder);

  if (!ladder?.enabled || !ladder.ranking) return "League standing";

  const ranking = ladder.ranking;

  if (ranking.algorithm === "elo")
    return "Elo rating · moves after every round";

  if (ranking.algorithm === "openskill")
    return "OpenSkill rating · settles as players play more";

  const window = ranking.window_rounds
    ? ` · last ${ranking.window_rounds} rounds`
    : "";

  switch (ranking.standing_aggregation) {
    case "ewma":
      return `Average round score · recent rounds count more${window}`;
    case "mean":
      return `Average round score${window}`;
    case "max":
      return `Best round score${window}`;
    case "min":
      return `Lowest round score${window}`;
    case "rated":
      return "Rating · moves after every round";
    case "latest":
      return "Latest grade · replaced on every grading round";
  }
}
